import { atom, atomFamily } from "recoil";

import type { ITableDto } from "../../dto";
import {
  ReportPeriodGroupingEnum,
  type ReportModalEditModeEnum,
  type ReportTemplateIdEnum,
} from "../../enums";
import type {
  ColumnOrderState,
  ColumnSizingState,
  GroupingState,
  PaginationState,
  SortingState,
  VisibilityState,
} from "../../helpers/react-table.features";
import {
  defaultReportPaginationState,
  type IReportApiDto,
  type IReportCriteriaApi,
  type IReportCriteriaFiltersSchemaValues,
  type IReportFiltersSchemaSettings,
  type IReportTemplateItem,
  type IReportTemplateItemBase,
} from "../../models";

export const reportDataAF = atomFamily<IReportApiDto | undefined, number>({
  key: "reportDataAF",
  default: undefined,
});

export const reportsOperationsDrawerAtom = atom<boolean>({
  key: "reportsOperationsDrawerAtom",
  default: false,
});

export const reportBulkOperationsDrawerAF = atomFamily<
  boolean,
  ReportTemplateIdEnum
>({
  key: "reportBulkOperationsDrawerAF",
  default: false,
});

export const reportsNavigationDrawerAtom = atom<boolean>({
  key: "reportsNavigationDrawerAtom",
  default: false,
});

export const reportsChartDrawerAtom = atom<boolean>({
  key: "reportsChartDrawerAtom",
  default: false,
});

export const reportsTableGroupingAF = atomFamily<GroupingState, number>({
  key: "reportsTableGroupingAF",
  default: () => [],
});

export const reportsTableSettingsDrawerAF = atomFamily<boolean, number>({
  key: "reportsTableSettingsDrawerAF",
  default: () => false,
});

export const reportFiltersWithValuesAF = atomFamily<
  IReportCriteriaFiltersSchemaValues,
  number
>({
  key: "reportFiltersWithValuesAF",
  default: () => ({}) as IReportCriteriaFiltersSchemaValues,
});

export const reportsFilterSettingsAF = atomFamily<
  IReportFiltersSchemaSettings,
  number
>({
  key: "reportsFilterSettingsAF",
  default: () => ({}) as IReportFiltersSchemaSettings,
});

export const reportsTableColumnsOrderStateAF = atomFamily<
  ColumnOrderState,
  number
>({
  key: "reportsTableColumnsOrderStateAF",
  default: () => [],
});

export const reportsTableColumnsVisibilityAF = atomFamily<
  VisibilityState,
  number
>({
  key: "reportsTableColumnsVisibilityAF",
  default: () => ({}),
});

/** Px width per column id; empty while the report grid still sizes its columns to the content. */
export const reportsTableColumnsSizingAF = atomFamily<
  ColumnSizingState,
  number
>({
  key: "reportsTableColumnsSizingAF",
  default: () => ({}),
});

export const reportsTableColumnsSortingAF = atomFamily<SortingState, number>({
  key: "reportsTableColumnsSortingAF",
  default: () => [],
});

export const reportsTableGroupingDirectionDescAF = atomFamily<boolean, number>({
  key: "reportsTableGroupingDirectionDescAF",
  default: () => false,
});

export const reportMenuItemsAtom = atom<IReportTemplateItem[] | undefined>({
  key: "reportMenuItemsAtom",
  default: undefined,
});

export const reportTemplatesAtom = atom<IReportTemplateItemBase[] | undefined>({
  key: "reportTemplatesAtom",
  default: undefined,
});

export const reportNavigationSearchAtom = atom<string>({
  key: "reportNavigationSearchAtom",
  default: "",
});

export const reportStartPageSearchAtom = atom<string>({
  key: "reportStartPageSearchAtom",
  default: "",
});

export const reportTableItemPaginationAF = atomFamily<
  PaginationState | null,
  number
>({
  key: "reportTableItemPaginationAF",
  default: defaultReportPaginationState,
});

export const reportTableRowsAF = atomFamily<ITableDto[], number>({
  key: "reportTableRowsAF",
  default: [],
});

export const rideFilterSearchAtom = atomFamily<string, number>({
  key: "rideFilterSearchAtom",
  default: "",
});

export const tableElementRefReportsAtom = atom<null | HTMLTableElement>({
  key: "tableElementRefReportsAtom",
  default: null,
});

export const reportModalEditAtom = atom<ReportModalEditModeEnum | false>({
  key: "reportModalEditAtom",
  default: false,
});

export const reportGroupingPeriodAF = atomFamily<
  ReportPeriodGroupingEnum,
  number
>({
  key: "reportGroupingPeriodAF",
  default: () => ReportPeriodGroupingEnum.EntirePeriod,
});

export const reportFlexGroupingParamsAF = atomFamily<string[], number>({
  key: "reportFlexGroupingParamsAF",
  default: () => [],
});

export const reportSelectedInnerDataIdsByReportIdAF = atomFamily<
  number[],
  number
>({
  key: "reportSelectedInnerDataIdsByReportIdAF",
  default: [],
});

export const reportLastSearchCriteriaByReportIdAF = atomFamily<
  IReportCriteriaApi | undefined,
  number
>({
  key: "reportLastSearchCriteriaByReportIdAF",
  default: undefined,
});
